import { type Vec3, cross, length, scale, vec3 } from './vec3';

/** Cuaternión (w, x, y, z). Las rotaciones se representan con cuaterniones unitarios. */
export interface Quat {
  readonly w: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export const IDENTITY: Quat = { w: 1, x: 0, y: 0, z: 0 };

export const quat = (w: number, x: number, y: number, z: number): Quat => ({ w, x, y, z });

export const conjugate = (q: Quat): Quat => ({ w: q.w, x: -q.x, y: -q.y, z: -q.z });

export const norm = (q: Quat): number => Math.sqrt(q.w * q.w + q.x * q.x + q.y * q.y + q.z * q.z);

export const normalize = (q: Quat): Quat => {
  const n = norm(q);
  return n === 0 ? IDENTITY : { w: q.w / n, x: q.x / n, y: q.y / n, z: q.z / n };
};

/** Normaliza solo si la norma se aparta de 1 más que `tolerance` (corrige deriva numérica). */
export const normalizeIfNeeded = (q: Quat, tolerance = 1e-9): Quat =>
  Math.abs(norm(q) - 1) > tolerance ? normalize(q) : q;

/** Producto de Hamilton: aplica `b` primero y luego `a` (a ∘ b). */
export const multiply = (a: Quat, b: Quat): Quat => ({
  w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
  x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
  y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
  z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
});

/** Rotación de `angle` radianes alrededor de `axis` (no necesita ser unitario). */
export const fromAxisAngle = (axis: Vec3, angle: number): Quat => {
  const n = length(axis);
  if (n === 0) return IDENTITY;
  const s = Math.sin(angle / 2) / n;
  return { w: Math.cos(angle / 2), x: axis.x * s, y: axis.y * s, z: axis.z * s };
};

/**
 * Vector de rotación (dirección = eje, módulo = ángulo en rad) a cuaternión.
 * Para ángulos muy pequeños usa la aproximación de primer orden.
 */
export const fromRotationVector = (r: Vec3): Quat => {
  const theta = length(r);
  if (theta < 1e-12) return normalize({ w: 1, x: r.x / 2, y: r.y / 2, z: r.z / 2 });
  return fromAxisAngle(r, theta);
};

/** Rota el vector `v` con el cuaternión unitario `q`: v' = q · (0, v) · q*. */
export const rotate = (q: Quat, v: Vec3): Vec3 => {
  // Forma optimizada: v' = v + 2w(u×v) + 2u×(u×v), con u = (x, y, z).
  const u = vec3(q.x, q.y, q.z);
  const t = scale(cross(u, v), 2);
  const c = cross(u, t);
  return vec3(v.x + q.w * t.x + c.x, v.y + q.w * t.y + c.y, v.z + q.w * t.z + c.z);
};

/** Rotación inversa: aplica q⁻¹ = q* a `v`. */
export const rotateInverse = (q: Quat, v: Vec3): Vec3 => rotate(conjugate(q), v);
